import React from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock,   InlineCode,   SectionTitle, MutedText, Note,  TableContainer,  Highlight, Image, PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider, Separator, CorrectionBox, Result} from '../../../../components/course/CourseComponents';
import screen2 from '../screen2.jpg';
import screen3 from '../screen3.jpg';

const Chapitre1Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Créer la boucle de jeu du Morpion, afficher la grille et détecter les clics sur les cases.
        </p>
      </InfoBox>      

      <Separator type="text" text="La boucle de jeu" color="gray-darker" size="md" />
      <SectionTitle>🎮 1/ La boucle de jeu</SectionTitle>
      
      <p>Comme pour le jeu précédent, on va créer le squelette de notre jeu.</p>

      <Note type="exercice" title="🧪 Crée le squelette du jeu :">
        Crée le squelette du jeu en ajoutant :<br/>
        - La fonction <InlineCode>setup</InlineCode>, qui va créer le canvas<br/>
        - La fonction <InlineCode>draw</InlineCode>, qui va colorier le fond de notre canvas<br/>
        - Et la fonction <InlineCode>mousePressed</InlineCode>, pour savoir quand le joueur clique<br/><br/>
        
        <Result>Un canvas de 600x600, avec une couleur de fond au choix.</Result>
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`function setup() {
  createCanvas(600, 600);
}

function draw() {
  background(220, 220, 220);
}

function mousePressed() {
}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <Separator type="text" text="Affichage de la grille" color="gray-darker" size="md" />
      <SectionTitle>📐 2/ Affichage de la grille</SectionTitle>

      <p>On va commencer par afficher la grille du morpion.</p>
      <p>Pour cela, on aura besoin des fonctions p5.js suivantes :</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>line(x1, y1, x2, y2)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Dessine une ligne.<br/>- Paramètres 1 et 2 sont les coordonnées du premier point<br/>- Paramètres 3 et 4 celles du deuxième point.</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>strokeWeight(taille)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Détermine l'épaisseur de la ligne.</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <p>On aimerait afficher 4 lignes :</p>
      <ul className="list-disc list-inside mb-4">
        <li>2 lignes verticales, affichées au ⅓ et ⅔ de la largeur de l'écran</li>
        <li>2 lignes horizontales, affichées à ⅓ et ⅔ de la hauteur de l'écran</li>
      </ul>

      Voila le resultat espéré : <br/>
      <Image src={screen2} alt="Grille du Morpion" width="300px" height="300px" borderRadius="xl" shadow={true} border={false} className="object-contain" />

  

      <Separator type="text" text="Lignes verticales" color="gray-darker" size="md" />
      <SubHeading>↕️ 3/ Lignes verticales</SubHeading>
      
      <p>On commence par determiner la largeur d'une case :</p>

      <CodeBlock language="javascript">
{`largeur_case = 600 / 3; // vaut 200`}
      </CodeBlock>

      La premiere ligne sera affichée à <InlineCode>largeur_case  * 1</InlineCode> et la seconde à <InlineCode>largeur_case * 2</InlineCode><br/>

      <p>Pour que notre largeur de case s'adapte à la taille de l'écran, on va utiliser les variables p5.js :</p>
      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Variable</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>width</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">largeur du canvas (ici 600)</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>height</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">hauteur du canvas (ici 600)</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <CodeBlock language="javascript">
{`largeur_case = width / 3;  // vaut toujours 200 mais s'adapte à la taille de l'écran`}
      </CodeBlock>

      <Image src={screen3} alt="Lignes verticales" width="300px" height="300px" borderRadius="xl" shadow={true} border={false} className="object-contain" />

      <Note type="exercice" title="🧪 Affiche les lignes verticales :">
        En utilisant <InlineCode>largeur_case</InlineCode> et en t'inspirant du schéma ci dessus, affiche les lignes verticales sur toute la hauteur de la fenêtre.<br/>
        - Ce code doit être placé dans la fonction <InlineCode>draw</InlineCode><br/>
        - Mettre l'épaisseur de la ligne à 6<br/><br/>
        <Result>Tu devrais avoir 2 lignes verticales</Result>
        <CorrectionBox>
          <CodeBlock language="javascript">
{`// Plateau de jeu
let largeur_case = width / 3;
strokeWeight(6);
line(largeur_case * 1, 0, largeur_case * 1, height);
line(largeur_case * 2, 0, largeur_case * 2, height);`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

   

      <Separator type="text" text="Lignes horizontales" color="gray-darker" size="md" />
      <SubHeading>↔️ 4/ Lignes horizontales</SubHeading>
      <p>C'est la meme chose ! On crée une variable <InlineCode>hauteur_case = height / 3</InlineCode> </p>

      <Note type="exercice" title="🧪 Affiche les lignes horizontales :">
        Affiche les 2 lignes horizontales, qui prendront toute la largeur de l'écran.<br/>
        - Ce code doit être placé dans la fonction <InlineCode>draw</InlineCode><br/>
        <br/>
        <Result>Tu devrais avoir un jolie plateau de jeu !</Result>
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`let hauteur_case = width / 3;
line(0, hauteur_case * 1, width, hauteur_case * 1);
line(0, hauteur_case * 2, width, hauteur_case * 2);`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

  
      
      <Separator type="text" text="Factorisation et amélioration" color="gray-darker" size="md" />
      <SubHeading>🔄 5/ Factorisation et amélioration</SubHeading>

      <p>On peut factoriser ce code (simplifier ce code en enlevant ce qui se répète)  en utilisant une boucle for.</p>
      <p>Comme tu l'as peut-être remarqué, le seul paramètre qui change entre la 1ère et la 2ème ligne, c'est le * 1 ou * 2.</p>

      <Note type="exercice" title="🧪 Utilise une boucle for :">
        Crée une boucle for qui va de 1 à 2, en remplaçant * 1 et * 2 par notre compteur.
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`strokeWeight(6);
for (let i = 1; i < 3; i++) {
  line(largeur_case * i, 0, largeur_case * i, height);
  line(0, hauteur_case * i, width, hauteur_case * i);
}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <p>On va aussi déplacer la déclaration des variables <InlineCode>largeur_case</InlineCode> et <InlineCode>hauteur_case</InlineCode> en global, c'est-à-dire tout en haut de notre code, car on va les utiliser plus tard dans d'autres fonctions.</p>
      

      <Note type="tip" title="💡 Variable locale / Variable globale">
        Les variables définies dans une fonction ont une <Highlight>portée locale</Highlight>, c'est-à-dire qu'on ne peut les utiliser que dans cette fonction. Si tu essaies de les utiliser ailleurs, tu auras une erreur car le programme ne les connaît pas.<br/><br/>
        
        Au contraire, si tu les déclares en global (en dehors de toute fonction, souvent en haut de ton code), tu pourras y accéder n'importe où dans ton code. Elles ont une <Highlight>portée globale</Highlight> !
      </Note>

      <Note type="exercice" title="🧪 Variables globales :">
        Déplace les variables <InlineCode>largeur_case</InlineCode> et <InlineCode>hauteur_case</InlineCode> en global, en haut de ton code.<br/>
        - Declare les variables avec <InlineCode>let</InlineCode>, sans leurs assigner de valeur.<br/>
        - Defini leur valeur dans la fonction <InlineCode>setup</InlineCode>, car <InlineCode>width</InlineCode> et <InlineCode>height</InlineCode> ne sont pas correctement définis avant le <InlineCode>createCanvas</InlineCode>.<br/>
        <br/>
        <Result>Tu devrais avoir un code plus propre !</Result>
        
        <CorrectionBox> 
          <CodeBlock language="javascript">
{`// Jeu 2 : le Morpion

let largeur_case;
let hauteur_case;

function setup() {
  createCanvas(600, 600);
  largeur_case = width / 3;
  hauteur_case = height / 3;
}`}
          </CodeBlock>
          </CorrectionBox>
      </Note>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ Code complet (le Plateau de jeu)</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 2 : le Morpion

let largeur_case;
let hauteur_case;

function setup() {
  createCanvas(600, 600);
  largeur_case = width / 3;
  hauteur_case = height / 3;
}

function draw() {
  background(220, 220, 220);

  // Plateau de jeu
  strokeWeight(6);
  for (let i = 1; i < 3; i++) {
    line(largeur_case * i, 0, largeur_case * i, height);
    line(0, hauteur_case * i, width, hauteur_case * i);
  }
}

function mousePressed() {

}`}
      </CodeBlock>

      <Note type="info" title="💡 Prêt pour la suite ?">
        Maintenant que tu as créé la grille du morpion, nous allons pouvoir commencer à ajouter la logique du jeu !
      </Note>



    </>
  );
};

export default Chapitre1Content; 